import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Card,
  Grid,
  Chip,
  alpha,
  useTheme,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from "@mui/material";
import {
  Link as LinkIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import ApproversInfo from './ApproversInfo';
import AILineMappingSummary from './AILineMappingSummary';
import { MAPPING_COLORS } from '../../../constants/purchaseInvoices/approvalPackages/approvalPackageDetailsConstants';
import { 
  formatPriceWithCurrency, 
  formatPrice, 
  getFormattedPrice,
  calculateUpdatedOrderData,
  getOrderDisplayText,
  getMappingColor
} from '../../../Utils/purchaseInvoices/approvalPackages/approvalPackageDetailsUtils';
import { getStyles } from '../../../styles/purchaseInvoices/approvalPackages/approvalPackageDetailsStyles';
import { useApprovalPackageDetails } from '../../../hooks/purchaseInvoices/approvalPackages/useApprovalPackageDetails';

const ApprovalPackageDetailsLayout = ({ data, refetchData, onClose, isRetainerInvoice = false }) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const successBg = alpha(theme.palette.success.main, 0.1);
  const errorBg = alpha(theme.palette.error.main, 0.05);
  
  const {
    showLineMappingMode,
    lineMappingCheckResult,
    selectedOrderIndex,
    mappings,
    saving,
    aiSuggestion,
    loadingAiSuggestion,
    displayMappingNumbers,
    mappingSource,
    lineMappingCompleted,
    lineMappingLoading,
    aiMappingLoadedRef,
    handleEditAiMapping,
    handleApproveAiMapping,
    getOrderItemMappingNumber,
    getInvoiceItemMappingNumbers,
    handleCheckLineMapping,
    enterLineMappingMode,
    exitLineMappingMode,
    handleOrderItemClick,
    handleInvoiceItemClick,
    removeMapping,
    isInvoiceItemMapped,
    getInvoiceItemMapping,
    saveMapping,
    clearMapping
  } = useApprovalPackageDetails(data, refetchData, onClose);

  // Don't display mapping numbers for retainer invoices
  const shouldDisplayMappingNumbers = displayMappingNumbers && !isRetainerInvoice;

  if (!data) {
    return (
      <Box sx={styles.container}>
        <Typography variant="body1" color="error">
          חסרים נתוני השוואה
        </Typography>
      </Box>
    );
  }
  
  const { invoiceId, order } = data;
  const updatedOrder = calculateUpdatedOrderData(order);

  const hasConvertedPrices = invoiceId?.items?.some(item => 
    item.hasConversion && 
    item.lineCurrency !== 'ILS' && 
    item.total_converted
  );

  const renderRegularTable = (items, showCode = true, isOrder = false, title) => {
    const isCreditInvoice = !isOrder && invoiceId?.isCreditInvoice;

    return (
      <Card sx={styles.card}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
          {title}
        </Typography>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={styles.tableContainer}
        >
          <Table>
            <TableHead sx={styles.tableHead}>
              <TableRow>
                {shouldDisplayMappingNumbers && (
                  <TableCell sx={{ width: '80px', textAlign: 'center', fontWeight: 'bold' }}>
                    שיוך
                  </TableCell>
                )}
                <TableCell>תיאור</TableCell>
                {showCode && <TableCell>קוד</TableCell>}
                <TableCell>כמות</TableCell>
                {isOrder && <TableCell>שער חליפין</TableCell>}
                <TableCell>מחיר יחידה</TableCell>
                {isOrder && <TableCell>סה"כ</TableCell>}
                {!isOrder && hasConvertedPrices && <TableCell>סה"כ</TableCell>}
                {!showCode && <TableCell>סה"כ כולל מע"מ</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items && items.map((item, index) => {
                const orderMappingNum = isOrder ? getOrderItemMappingNumber(index) : null;
                const invoiceMappingNums = !isOrder ? getInvoiceItemMappingNumbers(index) : [];
                
                return (
                  <TableRow key={index} sx={styles.tableRow}>
                    {shouldDisplayMappingNumbers && (
                      <TableCell sx={{ textAlign: 'center' }}>
                        {isOrder ? (
                          orderMappingNum && (
                            <Chip 
                              label={orderMappingNum}
                              size="small"
                              sx={styles.mappingChip(orderMappingNum, getMappingColor(orderMappingNum, MAPPING_COLORS))}
                            />
                          )
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {invoiceMappingNums.map((num, idx) => (
                              <Chip 
                                key={idx}
                                label={num}
                                size="small"
                                sx={styles.mappingChipLight(num, getMappingColor(num, MAPPING_COLORS))}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                    )}
                    
                    <TableCell>{item.description || item.PDES}</TableCell>
                    {showCode && <TableCell>{item.code || item.ICODE}</TableCell>}
                    <TableCell>
                      {item.ORIGINAL_QUANTITY 
                        ? `${item.quantity || item.TQUANT}/${item.ORIGINAL_QUANTITY}`
                        : (item.quantity || item.TQUANT)
                      }
                    </TableCell>
                    {isOrder && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.EXCH || 1}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {isOrder 
                        ? formatPriceWithCurrency(item.unit_price || item.PRICE, item.ICODE)
                        : formatPriceWithCurrency(item.unit_price || item.PRICE, item.lineCurrency || invoiceId?.currencyAnalysis?.itemsPricingCurrency?.code || invoiceId?.currencyCode || 'שח', null, null, isCreditInvoice)
                      }
                    </TableCell>
                    {isOrder && (
                      <TableCell>
                        {formatPriceWithCurrency(item.total || item.QPRICE, order?.CODE)}
                      </TableCell>
                    )}
                    {!isOrder && hasConvertedPrices && (
                      <TableCell>
                        {(() => {
                          const displayCurrency = invoiceId?.currencyAnalysis?.displayCurrency || invoiceId?.currencyCode || 'ILS';
                          const shouldShowConverted = item.hasConversion && (item.total_converted !== undefined && item.total_converted !== null) && displayCurrency === 'ILS';
                          const price = shouldShowConverted ? item.total_converted : (item.total || item.QPRICE);
                          const currency = shouldShowConverted ? 'ILS' : (item.lineCurrency || invoiceId?.currencyAnalysis?.itemsPricingCurrency?.code || invoiceId?.currencyCode || 'שח');
                          return formatPriceWithCurrency(price, currency, null, null, isCreditInvoice);
                        })()}
                      </TableCell>
                    )}
                    {!showCode && (
                      <TableCell>
                        {isOrder 
                          ? formatPriceWithCurrency(item.VATPRICE, order?.CODE)
                          : formatPriceWithCurrency(item.VATPRICE, invoiceId?.currencyCode || 'שח', null, null, isCreditInvoice)
                        }
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {!showLineMappingMode && (
          <Box sx={{ mt: 2, px: 2, pb: 2 }}>
            {isOrder ? 
              renderTotals(updatedOrder?.totals || order?.totals, order?.CODE, false) :
              renderTotals(invoiceId?.totals, invoiceId?.currencyAnalysis?.displayCurrency || invoiceId?.currencyCode, true)
            }
          </Box>
        )}
      </Card>
    );
  };

  const renderMappingTable = (items, isOrder = false, title) => {
    const isClickable = isOrder ? true : selectedOrderIndex !== null;
    const isCreditInvoice = !isOrder && invoiceId?.isCreditInvoice;

    return (
      <Card sx={styles.card}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2
        }}>
          <Typography variant="h6" component="h3" sx={{ 
            fontWeight: 'bold',
            color: isOrder ? 'primary.main' : 'success.main'
          }}>
            {title} ({items?.length || 0})
          </Typography>
          {!isOrder && (
            <Typography variant="body2" color="text.secondary">
              {isClickable ? 'לחץ על שורה לשיוך' : 'בחר תחילה שורה מההזמנה'}
            </Typography>
          )}
          {isOrder && (
            <Typography variant="body2" color="text.secondary">
              לחץ על שורה לבחירה
            </Typography>
          )}
        </Box>
        
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={styles.mappingTableContainer(isOrder)}
        >
          <Table>
            <TableHead sx={styles.mappingTableHead(theme, isOrder)}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>סטטוס</TableCell>
                {shouldDisplayMappingNumbers && (
                  <TableCell sx={{ width: '80px', textAlign: 'center', fontWeight: 'bold' }}>
                    שיוך
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 'bold' }}>תיאור</TableCell>
                {!isOrder && <TableCell sx={{ fontWeight: 'bold' }}>קוד</TableCell>}
                <TableCell sx={{ fontWeight: 'bold' }}>כמות</TableCell>
                {isOrder && <TableCell sx={{ fontWeight: 'bold' }}>שער חליפין</TableCell>}
                <TableCell sx={{ fontWeight: 'bold' }}>מחיר יחידה</TableCell>
                {isOrder && <TableCell sx={{ fontWeight: 'bold' }}>סה"כ</TableCell>}
                {!isOrder && hasConvertedPrices && <TableCell sx={{ fontWeight: 'bold' }}>סה"כ</TableCell>}
                {isOrder && <TableCell sx={{ fontWeight: 'bold' }}>סה"כ כולל מע"מ</TableCell>}
                <TableCell sx={{ fontWeight: 'bold' }}>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items && items.map((item, index) => {
                const isSelected = isOrder && selectedOrderIndex === index;
                const hasMapping = isOrder && mappings.some(m => m.orderLineIndex === index);
                const isMapped = !isOrder && isInvoiceItemMapped(index);
                const mapping = !isOrder && getInvoiceItemMapping(index);
                
                const orderMappingNum = isOrder ? getOrderItemMappingNumber(index) : null;
                const invoiceMappingNums = !isOrder ? getInvoiceItemMappingNumbers(index) : [];
                
                return (
                  <TableRow 
                    key={index} 
                    sx={styles.mappingTableRow(theme, isOrder, isClickable, isSelected, hasMapping, isMapped)}
                    onClick={() => {
                      if (isOrder) {
                        handleOrderItemClick(index);
                      } else if (isClickable) {
                        handleInvoiceItemClick(index);
                      }
                    }}
                  >
                    <TableCell>
                      {isOrder ? (
                        isSelected ? (
                          <Chip label="נבחר" size="small" color="primary" variant="filled" />
                        ) : hasMapping ? (
                          <Chip label="משויך" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="לא משויך" size="small" variant="outlined" />
                        )
                      ) : (
                        isMapped && mapping ? (
                          <Chip 
                            label={`משויך לשורה ${mapping.orderLineIndex + 1}`} 
                            size="small" 
                            color="success" 
                            variant="filled"
                          />
                        ) : (
                          <Chip label="לא משויך" size="small" variant="outlined" />
                        )
                      )}
                    </TableCell>
                    
                    {shouldDisplayMappingNumbers && (
                      <TableCell sx={{ textAlign: 'center' }}>
                        {isOrder ? (
                          orderMappingNum && (
                            <Chip 
                              label={orderMappingNum}
                              size="small"
                              sx={styles.mappingChip(orderMappingNum, getMappingColor(orderMappingNum, MAPPING_COLORS))}
                            />
                          )
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {invoiceMappingNums.map((num, idx) => (
                              <Chip 
                                key={idx}
                                label={num}
                                size="small"
                                sx={styles.mappingChipLight(num, getMappingColor(num, MAPPING_COLORS))}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                    )}
                    
                    <TableCell>
                      {isOrder ? 
                        (item.PDES || item.description || `פריט ${index + 1}`) :
                        (item.description || `פריט ${index + 1}`)
                      }
                    </TableCell>
                    
                    {!isOrder && <TableCell>{item.code || item.ICODE}</TableCell>}
                    
                    <TableCell>
                      {isOrder ? 
                        (item.ORIGINAL_QUANTITY 
                          ? `${item.quantity || item.TQUANT}/${item.ORIGINAL_QUANTITY}`
                          : (item.quantity || item.TQUANT)
                        ) : 
                        item.quantity
                      }
                    </TableCell>
                    
                    {isOrder && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.EXCH || 1}
                        </Typography>
                      </TableCell>
                    )}
                    
                    <TableCell>
                      {isOrder 
                        ? formatPriceWithCurrency(item.unit_price || item.PRICE, item.ICODE)
                        : formatPriceWithCurrency(item.unit_price || item.PRICE, item.lineCurrency || invoiceId?.currencyAnalysis?.itemsPricingCurrency?.code || invoiceId?.currencyCode || 'שח', null, null, isCreditInvoice)
                      }
                    </TableCell>
                    
                    {isOrder && (
                      <TableCell>
                        {formatPriceWithCurrency(item.total || item.QPRICE, order?.CODE)}
                      </TableCell>
                    )}
                    
                    {!isOrder && hasConvertedPrices && (
                      <TableCell>
                        {(() => {
                          const displayCurrency = invoiceId?.currencyAnalysis?.displayCurrency || invoiceId?.currencyCode || 'ILS';
                          const shouldShowConverted = item.hasConversion && item.total_converted !== undefined && item.total_converted !== null && displayCurrency === 'ILS';
                          const price = shouldShowConverted ? item.total_converted : (item.total || item.QPRICE);
                          const currency = shouldShowConverted ? 'ILS' : (item.lineCurrency || invoiceId?.currencyAnalysis?.itemsPricingCurrency?.code || invoiceId?.currencyCode || 'שח');
                          return formatPriceWithCurrency(price, currency, null, null, isCreditInvoice);
                        })()}
                      </TableCell>
                    )}
                    
                    {isOrder && (
                      <TableCell>
                        {formatPriceWithCurrency(item.VATPRICE, order?.CODE)}
                      </TableCell>
                    )}
                    
                    <TableCell>
                      {!isOrder && isMapped && mapping && (
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMapping(mapping.orderLineIndex, index);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {!showLineMappingMode && (
          <div className="mt-4">
            {isOrder ? 
              renderTotals(updatedOrder?.totals || order?.totals, order?.CODE, false) :
              renderTotals(invoiceId?.totals, invoiceId?.currencyAnalysis?.displayCurrency || invoiceId?.currencyCode, true)
            }
          </div>
        )}
      </Card>
    );
  };

  const renderTotals = (totals, currency = 'שח', isInvoice = false) => {
    const isCreditInvoice = isInvoice && invoiceId?.isCreditInvoice;
    
    const canCompare = invoiceId?.totals && order?.totals;
    const orderTotals = updatedOrder?.totals || order?.totals;
    const invoiceTotals = invoiceId?.totals;
    const invoiceSubtotalValue = Math.abs(invoiceTotals?.subtotal || invoiceTotals?.QPRICE || 0);
    const orderSubtotalValue = Math.abs(orderTotals?.subtotal || orderTotals?.QPRICE || 0);
    const invoiceTotalValue = Math.abs(invoiceTotals?.total || invoiceTotals?.TOTPRICE || 0);
    const orderTotalValue = Math.abs(orderTotals?.total || orderTotals?.TOTPRICE || 0);
    
    const subtotalDifference = Math.abs(invoiceSubtotalValue - orderSubtotalValue);
    const totalDifference = Math.abs(invoiceTotalValue - orderTotalValue);
    
    const subtotalMatches = canCompare && subtotalDifference <= 1;
    const totalWithVatMatches = canCompare && totalDifference <= 1;
    
    return (
      <>
        {totals && <Stack spacing={1} alignItems="flex-end" sx={styles.totalsStack}>
          <Typography 
            sx={{
              px: 1,
              borderRadius: 1,
              bgcolor: canCompare && subtotalMatches ? successBg : 
                     (canCompare ? errorBg : 'transparent')
            }}
          >
            סה"כ לפני מע"מ: {formatPriceWithCurrency(totals.subtotal || totals.QPRICE, currency, null, null, isCreditInvoice)}
          </Typography>
          <Typography>
            מע"מ: {formatPriceWithCurrency(totals.vat_amount || totals.VAT, currency, null, null, isCreditInvoice)}
          </Typography>
          <Typography 
            variant="h6" 
            color="primary"
            sx={{
              px: 1,
              borderRadius: 1,
              bgcolor: canCompare && totalWithVatMatches ? successBg : 
                     (canCompare ? errorBg : 'transparent')
            }}
          >
            סה"כ כולל מע"מ: {formatPriceWithCurrency(totals.total || totals.TOTPRICE, currency, null, null, isCreditInvoice)}
          </Typography>
        </Stack>}
      </>
    );
  };

  return (
    <Box sx={styles.container}>
      {invoiceId?.isCreditInvoice && (
        <Box sx={styles.alertBox}>
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              חשבונית זיכוי
            </Typography>
          </Alert>
        </Box>
      )}

      {order?.MULTIPLE_ORDERS && (
        <Box sx={styles.multipleOrdersAlert}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            חשבונית זו מכילה פריטים ממספר הזמנות: {order.ALL_ORDERS?.join(', ')}
          </Typography>
        </Box>
      )}

      {data?.goodsReceiptPriceInfo?.hasPriceDifferences && (
        <Box sx={styles.alertBox}>
          <Alert 
            severity="info" 
            icon={<WarningIcon />}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              {data.goodsReceiptPriceInfo.infoMessage}
            </Typography>
            {data.goodsReceiptPriceInfo.priceDifferences && data.goodsReceiptPriceInfo.priceDifferences.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  פרטי השינויים:
                </Typography>
                {data.goodsReceiptPriceInfo.priceDifferences.slice(0, 3).map((diff, idx) => (
                  <Box key={idx} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" display="block">
                      • {diff.description || diff.icode}: {formatPriceWithCurrency(diff.orderPrice * (diff.orderExch || 1), 'ILS')} → {formatPriceWithCurrency(diff.receiptPrice * (diff.receiptExch || 1), 'ILS')}
                    </Typography>
                    {diff.hasExchDifference && (
                      <Typography variant="caption" display="block" sx={{ ml: 2, color: 'warning.main', fontWeight: 'medium' }}>
                        שער חליפין: {diff.orderExch?.toFixed(4)} → {diff.receiptExch?.toFixed(4)} ({diff.orderPrice} × {diff.quantity} = {(diff.orderPrice * diff.quantity * diff.orderExch).toFixed(2)} ₪ → {(diff.receiptPrice * diff.quantity * diff.receiptExch).toFixed(2)} ₪)
                      </Typography>
                    )}
                  </Box>
                ))}
                {data.goodsReceiptPriceInfo.priceDifferences.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    ועוד {data.goodsReceiptPriceInfo.priceDifferences.length - 3} פריטים...
                  </Typography>
                )}
              </Box>
            )}
            {data.goodsReceiptPriceInfo.hasExchDifferences && (
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'warning.main', fontWeight: 'bold' }}>
                ⚠️ זוהו הבדלים בשער החליפין - בקליטה יילקח שער החליפין מקבלת הסחורה
              </Typography>
            )}
            {data.goodsReceiptPriceInfo.pricesMatchAfterUpdate !== undefined && (
              <Box sx={{ mt: 1, p: 1, bgcolor: data.goodsReceiptPriceInfo.pricesMatchAfterUpdate ? 'success.light' : 'warning.light', borderRadius: 1 }}>
                {data.goodsReceiptPriceInfo.pricesMatchAfterUpdate ? (
                  <Typography variant="caption" display="block" sx={{ color: 'success.dark', fontWeight: 'bold' }}>
                    ✅ המחירים המעודכנים מקבלת הסחורה תואמים את החשבונית
                  </Typography>
                ) : (
                  <Typography variant="caption" display="block" sx={{ color: 'warning.dark', fontWeight: 'bold' }}>
                    ⚠️ הפרש של {data.goodsReceiptPriceInfo.priceDifference?.toFixed(2)} ₪ בין המחירים המעודכנים לחשבונית
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
        </Box>
      )}

      {data?.exchangeRateOnlyDifference?.detected && (
        <Box sx={styles.alertBox}>
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              ✅ ניתן לאשר - הבדל בשער מטבע בלבד
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {data.exchangeRateOnlyDifference.message}
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: 'success.dark' }}>
              התאמה: {data.exchangeRateOnlyDifference.matchPercentage?.toFixed(1)}% מהפריטים תואמים במטבע מקורי
            </Typography>
          </Alert>
        </Box>
      )}

      {lineMappingCheckResult && (
        <Box sx={styles.alertBox}>
          {lineMappingCheckResult.requiresLineMapping ? (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleCheckLineMapping}
                    disabled={lineMappingLoading}
                  >
                    בדוק מחדש
                  </Button>
                  {!showLineMappingMode ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={enterLineMappingMode}
                    >
                      התחל שיוך
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={exitLineMappingMode}
                    >
                      חזור לתצוגה רגילה
                    </Button>
                  )}
                </Box>
              }
            >
              <Typography variant="body2">
                {lineMappingCheckResult.recommendation}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="body2">
                {lineMappingCheckResult.recommendation}
              </Typography>
            </Alert>
          )}
        </Box>
      )}
      
      {data?.requiresLineMapping && data.lineMappingCompleted && (
        <Box sx={styles.alertBox}>
          <Alert 
            severity="success" 
            action={
              <Button
                variant="outlined"
                size="small"
                startIcon={<LinkIcon />}
                onClick={enterLineMappingMode}
              >
                צפה בשיוך
              </Button>
            }
          >
            שיוך השורות הושלם בהצלחה
          </Alert>
        </Box>
      )}

      {showLineMappingMode && (
        <Box sx={{ mb: 3 }}>
          <Paper sx={styles.mappingPaper}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                מצב שיוך שורות פעיל
              </Typography>
              <Typography variant="body2">
                בחר שורה מההזמנה (משמאל), ולאחר מכן בחר שורות מתאימות מהחשבונית (מימין)
              </Typography>
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearMapping}
                  disabled={saving || mappings.length === 0}
                  color="secondary"
                >
                  נקה הכל
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={exitLineMappingMode}
                  disabled={saving}
                >
                  ביטול
                </Button>
              </Box>
              
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={saveMapping}
                disabled={saving || mappings.length === 0}
                size="large"
              >
                {saving ? 'שומר...' : 'שמור שיוך'}
              </Button>
            </Box>

            {mappings.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  שיוכים פעילים ({mappings.length}):
                </Typography>
                {mappings.map((mapping, index) => {
                  const orderItem = (updatedOrder?.items || order?.items || [])[mapping.orderLineIndex];
                  return (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 0.5 
                    }}>
                      <Typography variant="body2">
                        שורת הזמנה {mapping.orderLineIndex + 1}: {orderItem?.PDES || orderItem?.description}
                        {' → '}
                        שורות חשבונית: {mapping.invoiceLineIndices.map(idx => idx + 1).join(', ')}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeMapping(mapping.orderLineIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {!showLineMappingMode && lineMappingCompleted && mappings.length > 0 && (
        <Box sx={styles.alertBox}>
          <Alert 
            severity="success"
            icon={<CheckCircleIcon />}
            action={
              <Button
                size="small"
                onClick={handleEditAiMapping}
                sx={{ fontWeight: 600 }}
              >
                ערוך שיוך
              </Button>
            }
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {mappingSource === 'MANUAL' 
                ? 'שיוך שורות ידני הסתיים בהצלחה'
                : 'שיוך שורות על ידי AI הסתיים בהצלחה'
              }
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {mappings.length} שורות שוייכו • החשבונית הועברה לממתין לאישור
            </Typography>
          </Alert>
        </Box>
      )}

      {!showLineMappingMode && data?.requiresLineMapping && !lineMappingCompleted && !isRetainerInvoice && (
        <AILineMappingSummary 
          aiSuggestion={aiSuggestion}
          loading={loadingAiSuggestion && !aiMappingLoadedRef.current}
          onEditMapping={handleEditAiMapping}
          onApproveMapping={handleApproveAiMapping}
        />
      )}

      <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
        <Grid item xs={12} md={6}>
          {showLineMappingMode ? 
            renderMappingTable(updatedOrder?.items || order?.items, true, `פרטי ${getOrderDisplayText(order)}`) :
            renderRegularTable(updatedOrder?.items || order?.items, false, true, `פרטי ${getOrderDisplayText(order)}`)
          }
        </Grid>

        <Grid item xs={12} md={6}>
          {showLineMappingMode ? 
            renderMappingTable(invoiceId?.items, false, "פרטי חשבונית") :
            renderRegularTable(invoiceId?.items, true, false, "פרטי חשבונית")
          }
        </Grid>
      </Grid>

      {data.status === 'מאושר' || data.status === 'אושר ונקלט' ||
        data.status === 'ממתין ליצירה בפריוריטי' ? (
        <ApproversInfo approvers={data.approvedBy || []} />
      ) : null}
    </Box>
  );
};

export default ApprovalPackageDetailsLayout;
